import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink, Info, Zap } from 'lucide-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { isApplyAriVariantSearch } from 'helpers/applyWithAriFeature';
import styles from './BriefDetailsSignUp.module.scss';
import WebOnly from 'components/NativeApp/WebOnly';
import BriefDetailsOnboarding from './BriefDetailsOnboarding';
import BriefDetailsApplyWithAri from './BriefDetailsApplyWithAri';

const EASY_APPLY_LABEL = 'Easy apply';
const EASY_APPLY_TOOLTIP = 'Apply quickly with Twine';
const EXTERNAL_LABEL = 'External';
const EXTERNAL_TOOLTIP = 'You apply on their site.';

export default function BriefDetailsSignUp({
  isExternal = false,
  popularityWarning = false,
  showInterest = false,
  routeId = '',
  locationSearch = '',
}) {
  const body = typeof document !== 'undefined' ? document.body : undefined;
  const heading = isExternal ? 'Apply' : 'Apply to this job';
  const applyLabel = isExternal ? EXTERNAL_LABEL : EASY_APPLY_LABEL;
  const applyTooltip = isExternal ? EXTERNAL_TOOLTIP : EASY_APPLY_TOOLTIP;
  const ApplyIcon = isExternal ? ExternalLink : Zap;
  // Browse-intent jobs take an "interest" application, not a standard pitch.
  const showApplyWithAri =
    !isExternal &&
    !showInterest &&
    isApplyAriVariantSearch(locationSearch) &&
    Boolean(routeId);

  // Design card replaces the whole Easy Apply email box (no Easy Apply pill).
  if (showApplyWithAri) {
    return (
      <BriefDetailsApplyWithAri
        routeId={routeId}
        popularityWarning={popularityWarning}
        search={locationSearch}
      />
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.titleRow}>
        <h2 className={styles.heading}>{heading}</h2>
        <OverlayTrigger
          placement="top"
          trigger={['hover', 'focus', 'click']}
          rootClose
          container={body}
          overlay={<Tooltip id="apply-source-tooltip">{applyTooltip}</Tooltip>}
        >
          <button
            type="button"
            className={`${styles.applyLabel} ${
              isExternal ? styles.applyLabelExternal : styles.applyLabelEasy
            }`}
            aria-label={`${applyLabel}. ${applyTooltip}`}
          >
            <ApplyIcon
              className={styles.applyIcon}
              size={12}
              strokeWidth={2.4}
              aria-hidden="true"
            />
            {applyLabel}
          </button>
        </OverlayTrigger>
      </div>
      <p className={styles.guidance}>
        Enter your email to apply. It takes a minute.
      </p>
      {popularityWarning && (
        <div className={styles.notice} role="status">
          <Info size={16} aria-hidden="true" className={styles.noticeIcon} />
          <p className={styles.noticeText}>
            This job has reached its application limit for free accounts.
            <WebOnly>
              {' '}
              Twine Pro members can still apply.{' '}
              <a href="/pricing/creative">See plans</a>
            </WebOnly>
          </p>
        </div>
      )}
      <BriefDetailsOnboarding
        signUpContainerClass={styles.signUpContainer}
        text={showInterest ? 'Show interest' : 'Apply now'}
      />
      <div className={styles.finePrint}>
        <p>
          By applying you agree to our <a href="/terms">Terms</a>.
        </p>
        <p>
          Already a member? <a href="/signin">Log in</a>
        </p>
      </div>
    </div>
  );
}

BriefDetailsSignUp.propTypes = {
  isExternal: PropTypes.bool,
  popularityWarning: PropTypes.bool,
  showInterest: PropTypes.bool,
  routeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locationSearch: PropTypes.string,
};
